import { liquids, modules, placements, type EditorDoc } from './editorModel.ts'

/**
 * 데크 그림 재료(DOM 없음) — 백엔드 편집기 `pages/recipe_editor.js` 의 `areaBoxes` · `deckView` 셈과
 * `recipe_editor_model.js` 의 `trayDecor` · `labwareShapes` 를 옮겼다(core@d50b8cf4).
 *
 * 🔴 **자리 · 크기는 데크 정의(`/resources/admin/decks/<deckId>.json`)와 문서의 랩웨어 정의에서만 온다.** 값이 없으면
 *    그 도형을 빼고(`unplaced` · 장식 없음) 지어 그리지 않는다.
 * 🔴 칠은 문서의 시약 색(`displayColor`) — 없으면 차례대로 정해진 팔레트. 글자는 React 가 넣으므로 따로 이스케이프하지 않는다.
 */

/** 글자로 옮겨도 되는 값만 글자로 — 객체는 `fallback`(「[object Object]」를 화면에 내지 않는다) */
const scalarText = (value: unknown, fallback = ''): string =>
  typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean'
    ? String(value)
    : fallback

type Json = Record<string, unknown>
const isObject = (value: unknown): value is Json =>
  typeof value === 'object' && value !== null && !Array.isArray(value)
const num = (value: unknown): number => (typeof value === 'number' ? value : 0)
const tuple = (value: unknown): [number, number] | null =>
  Array.isArray(value) && typeof value[0] === 'number' && typeof value[1] === 'number'
    ? [value[0], value[1]]
    : null

export interface AreaBox {
  id: string
  type: string
  name: string
  x: number
  y: number
  w: number
  h: number
}

/** 데크 정의 → 자리 상자(데크 좌표 · mm) */
export function areaBoxes(deck: Json | null | undefined): AreaBox[] {
  if (!isObject(deck)) return []
  const locations = isObject(deck['locations']) ? deck['locations'] : {}
  const cutouts = Array.isArray(locations['cutouts']) ? (locations['cutouts'] as unknown[]) : []
  const cut: Record<string, unknown> = {}
  for (const c of cutouts)
    if (isObject(c) && typeof c['id'] === 'string') cut[c['id']] = c['position']
  const where: Record<string, unknown> = {}
  const fixtures = Array.isArray(deck['cutoutFixtures'])
    ? (deck['cutoutFixtures'] as unknown[])
    : []
  for (const f of fixtures) {
    const provides =
      isObject(f) && isObject(f['providesAddressableAreas']) ? f['providesAddressableAreas'] : {}
    for (const [cutId, areas] of Object.entries(provides))
      if (Array.isArray(areas))
        for (const a of areas) if (typeof a === 'string') where[a] = cut[cutId]
  }
  const areas = Array.isArray(locations['addressableAreas'])
    ? (locations['addressableAreas'] as unknown[])
    : []
  return areas.filter(isObject).map((a) => {
    const id = String(a['id'])
    const p = Array.isArray(where[id]) ? (where[id] as unknown[]) : [0, 0, 0]
    const offset = Array.isArray(a['offsetFromCutoutFixture'])
      ? (a['offsetFromCutoutFixture'] as unknown[])
      : [0, 0, 0]
    const box = isObject(a['boundingBox']) ? a['boundingBox'] : {}
    return {
      id,
      type: scalarText(a['areaType']),
      name: scalarText(a['displayName'], id),
      x: num(p[0]) + num(offset[0]),
      y: num(p[1]) + num(offset[1]),
      w: num(box['xDimension']),
      h: num(box['yDimension']),
    }
  })
}

export type LabwareShape =
  | { kind: 'pocket'; x: number; y: number; w: number; h: number; balance: boolean }
  | { kind: 'handle'; x: number; y: number; size: number; rotation: number }
  | { kind: 'well'; name: string; x: number; y: number; shape: 'circular'; diameter: number }
  | { kind: 'well'; name: string; x: number; y: number; shape: 'rectangular'; w: number; h: number }

/**
 * 하이픽 챔버 트레이 장식 — 칸(평형 칸 표시)과 손잡이. 편집층 `labware.<id>.metadata_ext` 에서만 읽는다(오픈트론 정의에 자리가 없다).
 * 값이 하나라도 없으면 null — 지어내지 않는다(그림은 웰만).
 */
function trayDecor(doc: EditorDoc, labwareId: string): LabwareShape[] | null {
  const ours = doc.designerApplication.data.labware?.[labwareId]
  const def = (doc.labwareDefinitions ?? {})[ours?.definitionUri ?? '']
  const ext = isObject(ours?.['metadata_ext']) ? ours['metadata_ext'] : null
  const given = ext !== null && isObject(ext['given_mm']) ? ext['given_mm'] : null
  const derived = ext !== null && isObject(ext['derived']) ? ext['derived'] : null
  const pockets = ext !== null && isObject(ext['pockets']) ? ext['pockets'] : null
  const balance =
    pockets !== null && isObject(pockets['balance_pocket']) ? pockets['balance_pocket'] : null
  const pocket = tuple(given?.['pocket'])
  const handle = tuple(given?.['handle'])
  const rotation = given?.['handle_rotation_deg']
  const handleCenter = tuple(derived?.['handle_center_mm'])
  const balanceCenter = tuple(balance?.['center_mm'])
  if (
    def?.wells === undefined ||
    pocket === null ||
    handle === null ||
    typeof rotation !== 'number' ||
    handleCenter === null ||
    balanceCenter === null
  )
    return null
  const [w, h] = pocket
  const out: LabwareShape[] = Object.values(def.wells).map((g) => ({
    kind: 'pocket',
    x: num(g['x']),
    y: num(g['y']),
    w,
    h,
    balance: false,
  }))
  out.push({ kind: 'pocket', x: balanceCenter[0], y: balanceCenter[1], w, h, balance: true })
  out.push({ kind: 'handle', x: handleCenter[0], y: handleCenter[1], size: handle[0], rotation })
  return out
}

/** 랩웨어 한 장의 도형(랩웨어 좌표) — 장식 + 정의 `wells` 차례. 정의가 없으면 빈 목록 */
export function labwareShapes(doc: EditorDoc, labwareId: string): LabwareShape[] {
  const ours = doc.designerApplication.data.labware?.[labwareId]
  const def = (doc.labwareDefinitions ?? {})[ours?.definitionUri ?? '']
  if (def?.wells === undefined) return []
  const out: LabwareShape[] = [...(trayDecor(doc, labwareId) ?? [])]
  for (const [name, g] of Object.entries(def.wells)) {
    out.push(
      g['shape'] === 'circular'
        ? {
            kind: 'well',
            name,
            x: num(g['x']),
            y: num(g['y']),
            shape: 'circular',
            diameter: num(g['diameter']),
          }
        : {
            kind: 'well',
            name,
            x: num(g['x']),
            y: num(g['y']),
            shape: 'rectangular',
            w: num(g['xDimension']),
            h: num(g['yDimension']),
          },
    )
  }
  return out
}

/** 팔레트 — 시약에 색이 없을 때 차례대로(백엔드 편집기와 같은 여덟) */
export const DECK_PALETTE = [
  '#4C9BD6',
  '#E0A030',
  '#9B6CC9',
  '#4FB28A',
  '#D65C5C',
  '#C9829B',
  '#5C7FD6',
  '#8FA35A',
] as const

export interface PlacedLabware {
  labwareId: string
  /** 랩웨어 외곽 왼쪽 아래(데크 좌표) */
  ox: number
  oy: number
  /** 놓인 모양의 가로 · 세로(돌렸으면 바뀐다) */
  lw: number
  lh: number
  /** 자리보다 길어 90° 돌려 놓았는가 */
  rotated: boolean
  /** 정의 원래 세로(돌린 좌표 셈에 쓴다) */
  defHeight: number
  shapes: LabwareShape[]
}

export interface DeckLayout {
  boxes: AreaBox[]
  placed: PlacedLabware[]
  /** 데크 정의에서 자리를 못 찾은 랩웨어(`id(꼴: 값)`) */
  unplaced: string[]
  /** `labwareId|well` → 칠 */
  fills: Record<string, string>
  bounds: { minX: number; minY: number; maxX: number; maxY: number }
}

/** 데크 그림 배치 — 자리 상자 가운데에 랩웨어를 놓는다(자리보다 길면 돌린다 · 백엔드 셈 그대로) */
export function deckLayout(doc: EditorDoc, deck: Json | null | undefined): DeckLayout | null {
  const boxes = areaBoxes(deck)
  if (boxes.length === 0) return null
  const byId = Object.fromEntries(boxes.map((b) => [b.id, b]))
  const moduleArea = Object.fromEntries(modules(doc).map((m) => [m.moduleId, m.location.value]))
  const fills: Record<string, string> = {}
  liquids(doc).forEach((l, i) => {
    const color = l.displayColor !== '' ? l.displayColor : DECK_PALETTE[i % DECK_PALETTE.length]!
    for (const load of l.loads)
      for (const well of Object.keys(load.volumeByWell)) fills[`${load.labwareId}|${well}`] = color
  })
  const defs = doc.labwareDefinitions ?? {}
  const placed: PlacedLabware[] = []
  const unplaced: string[] = []
  for (const p of placements(doc)) {
    const areaId =
      p.location.kind === 'module' ? moduleArea[p.location.value ?? ''] : p.location.value
    const box = areaId == null ? undefined : byId[areaId]
    const dim = defs[p.definitionUri]?.dimensions
    if (box === undefined || dim === undefined) {
      unplaced.push(`${p.labwareId}(${p.location.kind}: ${String(p.location.value)})`)
      continue
    }
    const fits = dim.xDimension <= box.w + 0.5 && dim.yDimension <= box.h + 0.5
    const rotated = !fits && dim.yDimension <= box.w + 0.5
    const lw = rotated ? dim.yDimension : dim.xDimension
    const lh = rotated ? dim.xDimension : dim.yDimension
    placed.push({
      labwareId: p.labwareId,
      ox: box.x + (box.w - lw) / 2,
      oy: box.y + (box.h - lh) / 2,
      lw,
      lh,
      rotated,
      defHeight: dim.yDimension,
      shapes: labwareShapes(doc, p.labwareId),
    })
  }
  const xs = [
    ...boxes.flatMap((b) => [b.x, b.x + b.w]),
    ...placed.flatMap((q) => [q.ox, q.ox + q.lw]),
  ]
  const ys = [
    ...boxes.flatMap((b) => [b.y, b.y + b.h]),
    ...placed.flatMap((q) => [q.oy, q.oy + q.lh]),
  ]
  return {
    boxes,
    placed,
    unplaced,
    fills,
    bounds: {
      minX: Math.min(...xs) - 10,
      minY: Math.min(...ys) - 10,
      maxX: Math.max(...xs) + 10,
      maxY: Math.max(...ys) + 10,
    },
  }
}

/** 랩웨어 좌표 → 데크 좌표(돌렸으면 90°) */
export function toDeck(q: PlacedLabware, x: number, y: number): [number, number] {
  return q.rotated ? [q.ox + (q.defHeight - y), q.oy + x] : [q.ox + x, q.oy + y]
}
